import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Button, Tooltip,
  Typography, CircularProgress, Alert, Chip, Stack, useTheme,
  Dialog, DialogTitle, DialogContent, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import { Refresh, BoltOutlined, EditOutlined, Close } from '@mui/icons-material';
import {
  LineChart, Line,
  BarChart, Bar,
  Tooltip as RechartTooltip,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import { ReportDefinition } from '../Settings/reportDsl';
import { reportsPanelToolbarButtonSx, reportsToolbarSurfaceBg } from './reportsToolbarSurface';
import { ReportPanelApiAction } from './ReportPanelApiAction';

interface RealtimePanelProps {
  report: ReportDefinition;
  tenantId: string;
  siteId?: string;
  timezone?: string;
  /** When provided, shows an edit icon in the header to configure the panel. */
  onEdit?: (report: ReportDefinition) => void;
  /** When false, hide the panel ingest-API (API Specs) button. Defaults to true. */
  showApiAction?: boolean;
}

type IdField = 'user_id' | 'anonymous_id' | 'session_id';
type ChartType = 'line' | 'bar';

interface RealtimeBucket {
  bucket: string;
  value: number;
  /** Epoch ms parsed from `bucket`, used as the dataKey for the X axis. */
  ts: number;
  /** Pre-formatted local-time label "HH:MM:SS". */
  label: string;
}

interface RealtimeSummary {
  windowSeconds: number;
  idField: IdField;
  value: number;
}

/** One identity active within a clicked bucket (realtime drilldown dialog). */
interface BucketUser {
  id: string;
  events: number;
}

/**
 * Approximate target spacing (px) between two adjacent x-axis ticks.
 * Smaller value -> more buckets shown on a wide screen.
 */
const TARGET_PX_PER_BUCKET = 80;
const MIN_BUCKETS = 6;
const MAX_BUCKETS = 60;

/**
 * Default realtime polling cadence + bucket size. Both can be overridden
 * per-panel via `visualizations[0].options.bucketSeconds`.
 *
 * 10s aligns with the brief: "Our Realtime Line graph will fetch the data
 * from Click House DB after every 10 seconds and display the count of users
 * in that 10 second time interval".
 */
const DEFAULT_BUCKET_SECONDS = 10;

/**
 * Default headline window. 15 minutes mirrors a Google Analytics-style
 * "active users in the last N minutes" pattern but matches the spec
 * ("active session ids in last 15 mins").
 */
const DEFAULT_SUMMARY_WINDOW_SECONDS = 900;
const DEFAULT_SUMMARY_ID_FIELD: IdField = 'user_id';

const ALLOWED_ID_FIELDS = new Set<IdField>(['user_id', 'anonymous_id', 'session_id']);
const ALLOWED_CHART_TYPES = new Set<ChartType>(['line', 'bar']);

/**
 * Stroke + fill used by both the line and bar variants. The same green tone
 * we already use across the realtime experience (chip color, etc.) so the
 * two chart types feel like the same panel in different display modes.
 */
const REALTIME_COLOR = '#10b981';

const formatLocalTime = (date: Date, timezone?: string): string => {
  try {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      timeZone: timezone || undefined,
    });
  } catch {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  }
};

/**
 * ClickHouse serializes DateTime as "YYYY-MM-DD HH:MM:SS" (UTC, no `T` /
 * `Z`). Browsers can't parse that directly, so we coerce to ISO-8601 + `Z`.
 */
const parseChDate = (raw: string): number => {
  if (!raw) return NaN;
  const isoish = /^\d{4}-\d{2}-\d{2} /.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw;
  const t = Date.parse(isoish);
  return Number.isFinite(t) ? t : NaN;
};

const idFieldNoun = (f: IdField, plural: boolean): string => {
  if (f === 'user_id') return plural ? 'users' : 'user';
  if (f === 'anonymous_id') return plural ? 'visitors' : 'visitor';
  return plural ? 'sessions' : 'session';
};

const formatWindowLabel = (totalSec: number): string => {
  if (totalSec >= 3600) {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  if (totalSec >= 60) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return s > 0 ? `${m}m ${s}s` : `${m} min`;
  }
  return `${totalSec}s`;
};

export const RealtimePanel: React.FC<RealtimePanelProps> = ({
  report, tenantId, siteId, timezone, onEdit, showApiAction = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const pathSite = useMemo(() => infralytiqsAnalyticsSitePathSegment(siteId), [siteId]);
  const toolbarIconButtonSx = reportsPanelToolbarButtonSx(theme, {
    minWidth: 36, width: 36, height: 36, p: 0, borderRadius: 1,
  });
  const panelToolbarButtonColor = theme.palette.mode === 'light' ? undefined : 'secondary' as const;

  // Realtime data is always "right now" — always render time labels in the
  // browser/system timezone so the X-axis and tooltip make immediate sense
  // to the viewer, regardless of the suite's configured server-side timezone
  // (which is often "UTC" and would otherwise show confusing UTC times).
  const effectiveTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const visOptions = (report.visualizations?.[0]?.options || {}) as Record<string, any>;
  const bucketSeconds = (() => {
    const raw = Number(visOptions.bucketSeconds);
    return Number.isFinite(raw) && raw >= 1 && raw <= 3600 ? Math.floor(raw) : DEFAULT_BUCKET_SECONDS;
  })();

  /**
   * Optional fixed chart window (seconds). When set, the x-axis always
   * covers this lookback (e.g. 1800 = last 30 minutes) by stretching
   * bucket width to `windowSeconds / bucketCount`. Poll cadence stays
   * on `bucketSeconds`. 0 / omitted keeps the legacy
   * `bucketSeconds * bucketCount` window.
   */
  const chartWindowSeconds = (() => {
    const raw = Number(visOptions.windowSeconds);
    return Number.isFinite(raw) && raw >= 1 && raw <= 86400 ? Math.floor(raw) : 0;
  })();

  const summaryWindowSeconds = (() => {
    const raw = Number(visOptions.summaryWindowSeconds);
    return Number.isFinite(raw) && raw >= 1 && raw <= 86400
      ? Math.floor(raw)
      : DEFAULT_SUMMARY_WINDOW_SECONDS;
  })();

  const summaryIdField: IdField = (() => {
    const raw = String(visOptions.summaryIdField || '').trim() as IdField;
    return ALLOWED_ID_FIELDS.has(raw) ? raw : DEFAULT_SUMMARY_ID_FIELD;
  })();

  // Visualization sub-type. The same realtime panel can render either a
  // line chart (emphasizes trend) or a bar chart (emphasizes per-bucket
  // "right now" values). The visualization *type* is authoritative:
  //   - `realtime_bar` → bar chart, full stop.
  //   - `realtime`     → honor `options.chartType` (line | bar), default line.
  //
  // We deliberately do NOT let `options.chartType` override `realtime_bar`.
  // Reports authored as `realtime` can carry a stale `chartType: 'line'`
  // option in their saved DSL; later changing the type to `realtime_bar`
  // would otherwise be silently ignored, leaving the panel rendering as a
  // line chart even though the user explicitly picked "Realtime Bar".
  const visType = report.visualizations?.[0]?.type;
  const chartType: ChartType = (() => {
    if (visType === 'realtime_bar') return 'bar';
    const raw = String(visOptions.chartType || '').trim() as ChartType;
    return ALLOWED_CHART_TYPES.has(raw) ? raw : 'line';
  })();

  // The metric defined on the report drives which "user" identifier we
  // count distinct on. We honor `query.metrics[0].field` when it's one of
  // the supported identifier columns; everything else falls back to user_id
  // (matches the server-side default).
  const idField: IdField = useMemo(() => {
    const f = report.query.metrics?.[0]?.field as string | undefined;
    return f && ALLOWED_ID_FIELDS.has(f as IdField) ? (f as IdField) : 'user_id';
  }, [report.query.metrics]);

  // The realtime endpoints don't run the full report query, so we honor the
  // one filter that matters for scoping live activity to a single domain:
  // a `page_url` containment filter authored on the report. We pass its
  // value to the server, which applies `page_url LIKE '%value%'` to both the
  // series/headline counts and the per-bucket drilldown.
  const pageUrlFilter: string | undefined = useMemo(() => {
    const filters = report.query.filters || [];
    const f = filters.find(
      (x) => x.field === 'page_url'
        && (x.op === 'contains' || x.op === 'eq' || x.op === 'starts_with'),
    );
    const v = f && typeof f.value === 'string' ? f.value.trim() : '';
    return v !== '' ? v : undefined;
  }, [report.query.filters]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [bucketCount, setBucketCount] = useState(MIN_BUCKETS);

  // Track viewport width by observing the panel's actual rendered size so
  // the x-axis density follows the column the panel was assigned in the
  // grid (full / half / one-third), not just the window. Falls back to
  // window.innerWidth when ResizeObserver isn't available.
  useEffect(() => {
    const compute = (width: number) => {
      const next = Math.round(width / TARGET_PX_PER_BUCKET);
      const clamped = Math.min(MAX_BUCKETS, Math.max(MIN_BUCKETS, next));
      setBucketCount((cur) => (cur === clamped ? cur : clamped));
    };

    if (!containerRef.current) return undefined;

    const initial = containerRef.current.clientWidth || window.innerWidth || 800;
    compute(initial);

    if (typeof ResizeObserver !== 'undefined') {
      const obs = new ResizeObserver((entries) => {
        for (const e of entries) {
          if (e.contentRect.width > 0) compute(e.contentRect.width);
        }
      });
      obs.observe(containerRef.current);
      return () => obs.disconnect();
    }

    const onResize = () => {
      if (containerRef.current) compute(containerRef.current.clientWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const apiBucketSeconds = chartWindowSeconds > 0
    ? Math.max(1, Math.round(chartWindowSeconds / Math.max(bucketCount, 1)))
    : bucketSeconds;

  const [data, setData] = useState<RealtimeBucket[]>([]);
  const [summary, setSummary] = useState<RealtimeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Per-bucket drilldown dialog: clicking a point on the chart resolves the
  // identities (id) that made up that bucket's count.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBucket, setDetailBucket] = useState<RealtimeBucket | null>(null);
  const [detailRows, setDetailRows] = useState<BucketUser[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailReqRef = useRef(0);

  const openBucketDetail = useCallback(async (bucket: RealtimeBucket) => {
    if (!tenantId || !bucket?.bucket) return;
    const myReq = ++detailReqRef.current;
    setDetailBucket(bucket);
    setDetailOpen(true);
    setDetailRows([]);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const qs = new URLSearchParams({
        bucket: bucket.bucket,
        bucketSeconds: String(apiBucketSeconds),
        idField,
      });
      if (pageUrlFilter) qs.set('pageUrl', pageUrlFilter);
      const res: any = await apiClient.get(
        `/il/analytics/${tenantId}/${pathSite}/realtime-users/detail?${qs.toString()}`,
      );
      if (myReq !== detailReqRef.current) return;
      const rows: BucketUser[] = (res?.data ?? [])
        .map((r: any) => ({
          id: String(r.id ?? ''),
          events: Number(r.events) || 0,
        }))
        // Hide anonymous / pre-login rows that carry no user id —
        // only identified users belong in the drilldown.
        .filter((u: BucketUser) => u.id !== '');
      setDetailRows(rows);
    } catch (err: any) {
      if (myReq !== detailReqRef.current) return;
      setDetailError(err?.message || 'Failed to load users for this point');
    } finally {
      if (myReq === detailReqRef.current) setDetailLoading(false);
    }
  }, [tenantId, pathSite, apiBucketSeconds, idField, pageUrlFilter]);

  // Normalizes the various shapes recharts hands us across its line/bar/dot
  // click callbacks into a `RealtimeBucket`. Depending on the element clicked
  // the datum may arrive directly, under `.payload`, or (chart-level click)
  // inside `activePayload[0].payload`.
  const resolveBucket = useCallback((arg: any): RealtimeBucket | undefined => {
    if (!arg) return undefined;
    const candidate =
      arg.activePayload?.[0]?.payload
      ?? arg.payload?.payload
      ?? arg.payload
      ?? arg;
    if (candidate && typeof candidate.bucket === 'string') {
      return candidate as RealtimeBucket;
    }
    // Chart-level click: map the active index back onto our series.
    const idx = arg.activeTooltipIndex;
    if (typeof idx === 'number' && data[idx]) return data[idx];
    return undefined;
  }, [data]);

  const handleChartClick = useCallback((state: any) => {
    const bucket = resolveBucket(state);
    if (bucket) openBucketDetail(bucket);
  }, [resolveBucket, openBucketDetail]);

  // Custom active dot so the highlighted point is itself clickable. In
  // recharts v3 the chart-level onClick no longer reliably carries the
  // active payload, so we bind the DOM click to the rendered dot directly.
  const renderActiveDot = useCallback((props: any) => {
    const { cx, cy, payload } = props || {};
    if (cx == null || cy == null) return <g />;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={REALTIME_COLOR}
        stroke="#fff"
        strokeWidth={2}
        cursor="pointer"
        onClick={(e) => {
          e.stopPropagation();
          const bucket = resolveBucket(payload);
          if (bucket) openBucketDetail(bucket);
        }}
      />
    );
  }, [resolveBucket, openBucketDetail]);

  // The custom `apiClient` is a thin fetch wrapper that doesn't support
  // request cancellation, so we use a monotonically increasing request id
  // to ignore the response of any older in-flight request when a newer one
  // returns first. Without this, a slow poll could overwrite a fresher one.
  const reqIdRef = useRef(0);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    const myReqId = ++reqIdRef.current;
    setError(null);
    try {
      const qs = new URLSearchParams({
        bucketSeconds: String(apiBucketSeconds),
        bucketCount: String(bucketCount),
        idField,
        summaryWindowSeconds: String(summaryWindowSeconds),
        summaryIdField,
      });
      if (pageUrlFilter) qs.set('pageUrl', pageUrlFilter);
      const res: any = await apiClient.get(
        `/il/analytics/${tenantId}/${pathSite}/realtime-users?${qs.toString()}`,
      );
      // Drop stale responses — only the latest request gets to update state.
      if (!isMountedRef.current || myReqId !== reqIdRef.current) return;
      const raw: Array<{ bucket: string; value: number }> = res?.data ?? [];
      const mapped: RealtimeBucket[] = raw.map((r) => {
        const ts = parseChDate(r.bucket);
        return {
          bucket: r.bucket,
          value: Number(r.value) || 0,
          ts,
          label: Number.isFinite(ts) ? formatLocalTime(new Date(ts), effectiveTimezone) : r.bucket,
        };
      }).filter((d) => Number.isFinite(d.ts));

      setData(mapped);
      // The server returns the headline aggregate alongside the series so
      // the "live users" number reflects activity over the configured
      // window (default 15 min), not just the most recent bucket.
      const s = res?.summary;
      if (s && typeof s === 'object') {
        const sIdField = ALLOWED_ID_FIELDS.has(s.idField as IdField)
          ? (s.idField as IdField)
          : summaryIdField;
        setSummary({
          windowSeconds: Number(s.windowSeconds) || summaryWindowSeconds,
          idField: sIdField,
          value: Number(s.value) || 0,
        });
      } else {
        setSummary(null);
      }
      setLastUpdated(new Date());
    } catch (err: any) {
      if (!isMountedRef.current || myReqId !== reqIdRef.current) return;
      setError(err?.message || 'Failed to load realtime data');
    }
}, [tenantId, pathSite, apiBucketSeconds, bucketCount, idField, summaryWindowSeconds, summaryIdField, pageUrlFilter]);

  // Initial load + bucketCount changes (viewport resize) trigger a refetch.
  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => {
      if (isMountedRef.current) setLoading(false);
    });
  }, [fetchData]);

  // Fixed-cadence polling. Set up once per (tenantId, pathSite, bucketSeconds)
  // tuple — re-creating the timer on every fetch would cause it to drift.
  // The actual fetch is read from a ref so the closure always sees the
  // latest function (which captures the latest bucketCount / idField).
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  useEffect(() => {
    if (!tenantId) return undefined;
    const id = window.setInterval(() => {
      fetchRef.current();
    }, bucketSeconds * 1000);
    return () => window.clearInterval(id);
  }, [tenantId, pathSite, bucketSeconds]);

  // Pretty subtitle that explains the moving window:
  //   "Updates every 10s · 18 × 10s window (-3:00 → now)"
  const totalWindowSec = apiBucketSeconds * bucketCount;
  const mins = Math.floor(totalWindowSec / 60);
  const secs = totalWindowSec % 60;
  const windowLabel = mins > 0 ? `-${mins}:${String(secs).padStart(2, '0')}` : `-${secs}s`;

  // Headline: prefer the server-computed summary (active <id>s over the
  // last `summaryWindowSeconds`), fall back to the most-recent bucket so
  // older deployments still render something sensible.
  const headlineValue = summary ? summary.value : (data.length > 0 ? data[data.length - 1].value : 0);
  const headlineIdField: IdField = summary ? summary.idField : idField;
  const headlineWindowSec = summaryWindowSeconds
    || (summary ? summary.windowSeconds : apiBucketSeconds);
  const headlineWindowLabel = formatWindowLabel(headlineWindowSec);

  // Shared chart axes / tooltip configuration so the line and bar variants
  // line up perfectly when the user toggles between them.
  const renderXAxis = () => (
    <XAxis
      dataKey="ts"
      type="number"
      domain={['dataMin', 'dataMax']}
      tick={{ fontSize: 11 }}
      tickFormatter={(v: number) => {
        if (!Number.isFinite(v)) return '';
        return formatLocalTime(new Date(v), effectiveTimezone);
      }}
      interval="preserveStartEnd"
      minTickGap={Math.max(24, Math.floor(TARGET_PX_PER_BUCKET / 2))}
      scale="time"
    />
  );

  const renderYAxis = () => (
    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
  );

  // Recharts uses fixed inline-styled defaults for the tooltip, so on dark
  // themes the popover renders with a near-white background and black text
  // — which clashes with the rest of the app. Style it explicitly from the
  // MUI palette so the tooltip matches the panel chrome in both themes.
  const tooltipContentStyle: React.CSSProperties = {
    backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[6],
    color: theme.palette.text.primary,
    padding: '6px 10px',
  };
  const tooltipLabelStyle: React.CSSProperties = {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginBottom: 2,
  };
  const tooltipItemStyle: React.CSSProperties = {
    color: theme.palette.text.primary,
  };
  const tooltipCursor = {
    stroke: theme.palette.divider,
    strokeWidth: 1,
    fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
  };

  const renderTooltip = () => (
    <RechartTooltip
      labelFormatter={(v: number) => Number.isFinite(v)
        ? formatLocalTime(new Date(v), effectiveTimezone)
        : ''}
      formatter={(val: number) => [val, idFieldNoun(idField, true).replace(/^./, (c) => c.toUpperCase())]}
      contentStyle={tooltipContentStyle}
      labelStyle={tooltipLabelStyle}
      itemStyle={tooltipItemStyle}
      cursor={tooltipCursor}
      wrapperStyle={{ outline: 'none' }}
    />
  );

  const detailNoun = idFieldNoun(idField, true).replace(/^./, (c) => c.toUpperCase());
  const renderDetailDialog = () => (
    <Dialog
      open={detailOpen}
      onClose={() => setDetailOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        {detailNoun} at {detailBucket ? detailBucket.label : ''}
        <Typography variant="caption" color="text.secondary" component="div">
          {detailBucket ? `${detailBucket.value.toLocaleString()} ${idFieldNoun(idField, detailBucket.value !== 1)} · ${bucketSeconds}s interval` : ''}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={() => setDetailOpen(false)}
          size="small"
          color="inherit"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'inherit' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {detailError && <Alert severity="error" sx={{ mb: 1 }}>{detailError}</Alert>}
        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : detailRows.length === 0 && !detailError ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No {idFieldNoun(idField, true)} found for this point.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailRows.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{u.id || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        className="il-panel-titlebar"
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: (t) => reportsToolbarSurfaceBg(t),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
          <BoltOutlined color="secondary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600} component="div">
            {report.visualizations?.[0]?.title || report.name || 'Realtime Users'}
          </Typography>
          <Chip size="small" label="realtime" color="secondary" variant="outlined" />
          <Chip size="small" label={chartType} variant="outlined" />
          <Chip size="small" label={`${bucketCount} × ${apiBucketSeconds}s`} variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <ReportPanelApiAction
            report={report}
            tenantId={tenantId}
            siteId={siteId}
            visible={showApiAction}
          />
          {onEdit && (
            <Tooltip title="Edit panel configuration">
              <Button
                variant="outlined"
                color={panelToolbarButtonColor}
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit(report); }}
                aria-label="Edit report panel"
                sx={toolbarIconButtonSx}
              >
                <EditOutlined fontSize="small" />
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <Button
              variant="outlined"
              color={panelToolbarButtonColor}
              size="small"
              onClick={() => fetchData()}
              aria-label="Refresh report"
              sx={toolbarIconButtonSx}
            >
              <Refresh fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {report.description
            ? `${report.description} · `
            : ''}
          Live · updates every {bucketSeconds}s · window {windowLabel} → now
          {lastUpdated ? ` · last update ${formatLocalTime(lastUpdated, timezone)}` : ''}
        </Typography>
      </Box>
      <CardContent ref={containerRef} sx={{ flex: 1, pt: 1, minWidth: 0, overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {/*
          Headline reflects the summary window (default: distinct sessions
          in the last 15 minutes), not just the most recent bucket on the
          chart — so the page always communicates "live activity" the same
          way Google Analytics' realtime view does.
        */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="h3" fontWeight={800} color="secondary">
            {headlineValue.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {String(visOptions.summaryCaption || '').trim()
              || `active ${idFieldNoun(headlineIdField, true)} in the last ${headlineWindowLabel}`}
          </Typography>
        </Box>

        {loading && data.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : chartType === 'bar' ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} onClick={handleChartClick} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              {renderXAxis()}
              {renderYAxis()}
              {renderTooltip()}
              <Bar
                dataKey="value"
                fill={REALTIME_COLOR}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
                cursor="pointer"
                onClick={(barData: any) => {
                  const bucket = resolveBucket(barData);
                  if (bucket) openBucketDetail(bucket);
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} onClick={handleChartClick} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              {renderXAxis()}
              {renderYAxis()}
              {renderTooltip()}
              <Line
                type="monotone"
                dataKey="value"
                stroke={REALTIME_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={renderActiveDot}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
      {renderDetailDialog()}
    </Card>
  );
};
